import BaseButton from '@/components/ui/BaseButton';
import styleVariables from '@/styles/style.module.scss';
import BaseCard from '@/components/ui/BaseCard';
import { Autocomplete, Box, CircularProgress, LinearProgress, Paper, TextField, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StoreIcon from '@mui/icons-material/Store';
import BaseTitle from '@/components/ui/BaseTitle';
import { useAppContext } from '@/provider/contexts';
import { useNavigate } from 'react-router-dom';
import { Store } from '@/types';
import BaseFormControl from '@/components/form/BaseFormControl';
import { t } from 'i18next';
import localforage from 'localforage';
import { fetchStoreListAPI } from '@/api/stores';

type StoreAutocompleteProps = {
    storeId: number | null;
    storeOptions: Store[];
    onStoreChange: (storeId: number) => void;
};

const StoreAutoComplete: FC<StoreAutocompleteProps> = ({ storeId, storeOptions, onStoreChange }) => {
    const { setManageStoreName } = useAppContext();
    return (
        <BaseFormControl fullWidth variant="outlined">
            <Autocomplete
                id="store-select"
                getOptionKey={(option) => option.store_id}
                value={storeId && storeOptions.length ? storeOptions.find((store) => store.store_id === storeId) : null}
                options={storeOptions}
                getOptionLabel={(option) => option.store_name}
                isOptionEqualToValue={(option, value) => option.store_id === value.store_id}
                loadingText={
                    <>
                        <LinearProgress />
                        <Typography sx={{ ml: 0.5, mt: 1.5 }}> {t('Loading Stores...')}</Typography>
                    </>
                }
                noOptionsText={t('No store data available.')}
                onChange={(_, value) => {
                    const selectedStore = storeOptions.find((store) => store.store_id === value?.store_id);
                    if (selectedStore) {
                        onStoreChange(selectedStore.store_id);
                        setManageStoreName(selectedStore.store_name);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('Store')}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: params.InputProps.endAdornment,
                            },
                        }}
                    />
                )}
                slots={{
                    paper: (props) => <Paper sx={{ maxHeight: '30vh', overflowY: 'auto' }} {...props} />,
                }}
            />
        </BaseFormControl>
    );
};

const MobileSelectionPage = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const [storeId, setStoreId] = useState<number | null>(null);
    const [storeOptions, setStoreOptions] = useState<Store[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const initialize = async () => {
            try {
                setIsLoading(true);
                const cid = (await localforage.getItem('corporation_id')) as number;
                const params = { cid: cid };
                const {
                    data: { data, isSuccess },
                } = await fetchStoreListAPI(params);
                if (isSuccess) {
                    const stores = data.aaData || [];
                    stores.sort((a: Store, b: Store) => {
                        if (a.store_name < b.store_name) return -1;
                        if (a.store_name > b.store_name) return 1;
                        return 0;
                    });
                    if (stores.length === 1) {
                        const storeId = stores[0].store_id;
                        localStorage.setItem('manageStoreId', String(storeId));
                        localStorage.setItem('hasMultiStore', 'false');
                        navigate(`/mobile/${storeId}`, { replace: true });
                        return;
                    }
                    if (stores.length > 0) {
                        localStorage.setItem('hasMultiStore', 'true');
                        setStoreOptions(stores);
                        return;
                    }
                }
                throw new Error();
            } catch (error) {
                console.error('err', error);
                localStorage.setItem('manageStoreId', '');
                localStorage.setItem('hasMultiStore', 'false');
                navigate(`/dashboard`, { replace: true });
                return;
            } finally {
                setIsLoading(false);
            }
        };
        initialize();
    }, []);

    const handleStoreConfirm = () => {
        if (storeId) {
            navigate(`/mobile/${storeId}`, { replace: true });
        }
    };

    return (
        <Paper sx={{ height: 'calc(100vh)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="subtitle1">{t('Going to the Store Selection...')}</Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        backgroundColor: styleVariables.bgColor1,
                    }}
                >
                    <BaseCard sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, width: '80%' }}>
                        <BaseTitle icon={<StoreIcon />} title={t('Select Store')} />
                        <StoreAutoComplete storeId={storeId} storeOptions={storeOptions} onStoreChange={setStoreId} />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <BaseButton className="confirm-button" onClick={handleStoreConfirm} disabled={!storeId}>
                                {t('CONFIRM')}
                            </BaseButton>
                        </Box>
                    </BaseCard>
                </Box>
            )}
        </Paper>
    );
};

export default MobileSelectionPage;
